import React, { useState, useEffect } from "react"
import DataTable from "aldoo-ra/DataTable"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import PageHeader from "aldoo-ra/Admin/PageHeader"
import Modal from "aldoo-ra/Modal"
import ContentEditor from "aldoo-ra/CMS/content-editor"
import { showNotification } from "aldoo-ra/Notification"
import DatabaseModelTypeResolver from "aldoo-ra/CMS/database-model-type-resolver"
import { PlusCircleIcon, TrashIcon, PencilIcon, ArrowUturnLeftIcon } from "@heroicons/react/24/outline"
import Tooltip from "aldoo-ra/Tooltip"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import RangeSelector from "aldoo-ra/RangeSelector"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import Icon from "aldoo-ra/Icon"
import StoreProductFormatter from "aldoo-ra/CMSModels/StoreProduct/formatters/store-product-formatter"
import { priceDisplayFormat } from "aldoo-ra/CMSModels/StorePrice/display-formatters"
import { twMerge } from "tailwind-merge"
import { getStoreProductTypeOptions, getProductTypeColor } from "./store-product-utils"
import { renderPlatformIcon, getPlatformDisplayName } from "aldoo-ra/Admin/utils/platformIconUtils"
import StoreProductRestoreDialog from "./RestoreDialog"
import { url } from "aldoo-ra/CMS/url"
import { useUrlParam } from "aldoo-ra/URLState"

import apiInterface from "aldoo-ra/CMSModels/StoreProduct/api"
import { api } from "aldoo-ra/API"
import { storeProductModel } from "aldoo-ra/Store/api"
import { applyStoreProductModel } from "aldoo-ra/Admin/Store/extensions"
import { useStoreCurrency } from "aldoo-ra/Store/currency"
import { EditingLanguageProvider, EditingLanguageSwitch, useEditingLanguageState } from "./EditingLanguage"
import useProductEditorTexts from "aldoo-ra/CMSModels/StoreProduct/useProductEditorTexts"
const {
  Get: GetStoreProducts,
  Save: SaveStoreProduct,
  SoftDelete: SoftDeleteProduct,
} = apiInterface


/**
 * The store products browser.
 *
 * `match` narrows it to a subset (the Vendors page shows one organization's
 * products) and `hideHeader` drops the page header when it is embedded in a
 * page that has one of its own. `columnOverrides`
 * ({ [columnId]: { title?, render?, … } }) replaces parts of a column (type,
 * platforms …) for such a host. A change of `refreshSignal` reloads the list
 * (a host that hears the products change elsewhere, as a vendor's view does).
 *
 * `selectionActions` lets the products be picked (a checkbox on each row) and
 * changed together: ({ ids, rows }) => [{ value, name, hint?, icon?, run }],
 * offered under "Actions" once something is picked; `run({ ids, rows, refresh,
 * clear })` does the change (the host's, as only it knows what may be done).
 *
 * `filters` adds a host's own filters to the header, before the browser's:
 * [{ id, render: ({ value, onChange }) => node, match: (value) => query }].
 * `hiddenFilters` drops the browser's own ("type", "platform", "price") — a
 * vendor filters by content type, not by the store's purchase type.
 *
 * `thumbnailBadge(item)` puts a host's mark on the product's picture, in its
 * corner (a vendor's review state, as a status dot); `nameBadge(item)` one
 * after its name (a chip saying it waits for something). An override of
 * `{ hidden: true }` leaves a column out. An override's `render(value, item,
 * { open })` may open the product: `open(tab)` shows it at that tab.
 *
 * `labels` replaces the browser's own words (DEFAULT_LABELS) — a host in
 * another language hands them over translated.
 *
 * `dialogOnly`: no list — only the product dialog, opened by the address
 * (?pid=), over whatever screen hosts it (a review opens its product in place).
 */
// the browser's own words, in English; a host may pass them translated (`labels`)
const DEFAULT_LABELS = {
  productName: "Product Name",
  price: "Price",
  type: "Type",
  linkedWithStore: "Linked with Store",
  actions: "Actions",
  filterByType: "Filter by type",
  filterByPlatform: "Filter by platform",
  priceMin: "Min",
  priceMax: "Max",
  priceRange: "Range",
  priceRangeInvalid: "The minimum is above the maximum",
  priceFilterFailed: "Failed to filter by price",
  search: "Search...",
  noProducts: "No products added yet",
  noPriceModel: "No price model",
  addPrice: "Add New Price",
  noPriceConfigured: "No price configured",
  noPlatforms: "No platforms configured",
  confirmDeleteTitle: "Confirm Delete",
  // %s: the product's name
  confirmDeleteMessage: 'Are you sure you want to delete "%s"?',
  thisProduct: "this product",
  delete: "Delete",
  cancel: "Cancel",
  deleted: "Product deleted",
  selected: "%s selected",
  deselect: "Deselect",
  actions: "Actions",
}

export default function ProductBrowser({
  match: matchProp,
  hideHeader = false,
  columnOverrides = null,
  refreshSignal = 0,
  filters = [],
  hiddenFilters = [],
  // what the empty table shows instead of the plain notice (a "New product" button)
  noData = null,
  thumbnailBadge = null,
  nameBadge = null,
  labels: labelOverrides = null,
  selectionActions = null,
  // the language the dialog opens every product in, when the store has it (EditingLanguage)
  defaultEditingLanguage = null,
  dialogOnly = false,
  // the help center's key of a tab of the product dialog, as a "?" beside its name
  // (aldoo-ra/HelpCenter/InPlaceHelp): the vendor portal passes (tab) => `vendor.product#${tab}`
  helpKeyOf = null,
}) {
  const labels = labelOverrides ? { ...DEFAULT_LABELS, ...labelOverrides } : DEFAULT_LABELS
  // the product dialog's words in the site's language (TextBundle store_product_editor)
  const t = useProductEditorTexts()
  const { getType } = DatabaseModelTypeResolver()
  const StoreProduct = getType("StoreProduct")
  const StorePrice = getType("StorePrice")

  const [selectedInstance, setSelectedInstance] = useState(null)
  // the one language the whole dialog edits in, picked at its top
  const editingLanguage = useEditingLanguageState(selectedInstance, { preferred: defaultEditingLanguage })
  const [selectedPrice, setSelectedPrice] = useState(null)
  // a new product's default price is in the store's currency (STORE_CURRENCY)
  const { currency: storeCurrency } = useStoreCurrency()
  const [refreshKey, setRefreshKey] = useState(0)
  // the products picked for a change together (selectionActions): their ids and rows
  const [selectedIds, setSelectedIds] = useState([])
  const [selectedRows, setSelectedRows] = useState([])
  const [bulkBusy, setBulkBusy] = useState(false)
  const clearSelection = () => {
    setSelectedIds([])
    setSelectedRows([])
  }
  // the host heard the products change (a review decided elsewhere)
  useEffect(() => {
    if (refreshSignal) setRefreshKey((key) => key + 1)
  }, [refreshSignal])
  const [platformFilter, setPlatformFilter] = useState([])
  const [typeFilter, setTypeFilter] = useState([])
  // { min?, max? } of the price, and the prices that fall in it (null: no filter)
  const [priceRange, setPriceRange] = useState(null)
  const [priceIds, setPriceIds] = useState(null)
  const priceRequest = React.useRef(0)
  // the host's filters, by id
  const [hostFilterValues, setHostFilterValues] = useState({})
  const [productTypeOptions, setProductTypeOptions] = useState([])
  const [showRestoreDialog, setShowRestoreDialog] = useState(false)
  // deep link: /admin/store/products?pid=<pid> opens that product
  const [pid, setPid] = useUrlParam("pid", { default: "" })
  // a deep link may say where it came from (?back=), so closing the dialog
  // returns there instead of stranding the visitor on this page
  const [back] = useUrlParam("back", { default: "" })
  // the product dialog keeps its open tab here; closing the dialog takes it
  // out of the URL with the product
  const [, setProductTab] = useUrlParam("ptab", { default: "" })

  const closeDialog = () => {
    setSelectedInstance(null)
    setPid("")
    setProductTab("")
    if (back && typeof window !== "undefined") window.location.href = back
  }

  // the address names another product (a link from a product's group): the dialog opens that one
  useEffect(() => {
    if (!pid || selectedInstance?.pid === pid) return
    GetStoreProducts({ match: { pid }, limit: 1 })
      .then((res) => {
        const item = Array.isArray(res) ? res[0] : res?.data?.[0]
        if (item) setSelectedInstance(item)
      })
      .catch(() => {
        // no such product: the browser stays as it is
      })
  }, [pid])

  // Compose the product model: the tabs of the enabled store product extensions
  useEffect(() => {
    storeProductModel()
      .then((res) => applyStoreProductModel(res?.enabled || []))
      .catch(() => {
        // the framework sections still render
      })
  }, [])

  // Load product types from ParamIO
  useEffect(() => {
    const loadProductTypes = async () => {
      try {
        const options = await getStoreProductTypeOptions()
        setProductTypeOptions(options)
      } catch (error) {
        console.error('Failed to load product types:', error)
        // Fallback to default options
        setProductTypeOptions([
          { value: "purchase", name: "Purchase" },
          { value: "subscription", name: "Subscription" },
          { value: "license", name: "License" },
          { value: "bundle", name: "Bundle" },
        ])
      }
    }
    loadProductTypes()
  }, [])

  const getPlatformFilter = () => {
    if (!platformFilter || !platformFilter.length) return {}

    const selectedPlatforms = platformFilter.map((t) => t.value)

    // Query products where price.price_model contains real_product_id with matching platforms
    return {
      "price.price_model": {
        $elemMatch: {
          "type": "real",
          "real_product_id": {
            $elemMatch: {
              "platform": { $in: selectedPlatforms }
            }
          }
        }
      }
    }
  }

  // The price is a reference, so the range is asked of the prices first and
  // the products are matched by the prices that fall in it
  const changePriceRange = async (range) => {
    setPriceRange(range)
    const request = ++priceRequest.current
    if (!range) {
      setPriceIds(null)
      setRefreshKey((prev) => prev + 1)
      return
    }
    const amount = {}
    if (range.min !== undefined) amount.$gte = range.min
    if (range.max !== undefined) amount.$lte = range.max
    try {
      const prices = await api("crud")({
        get: {
          model: "StorePrice",
          match: { price_model: { $elemMatch: { amount } } },
          select: "_id",
          itemsPerPage: 10000,
        },
      })
      if (request !== priceRequest.current) return
      setPriceIds((Array.isArray(prices) ? prices : prices?.data || []).map((price) => price._id))
      setRefreshKey((prev) => prev + 1)
    } catch (error) {
      if (request !== priceRequest.current) return
      showNotification({
        message: labels.priceFilterFailed,
        className: "bg-admin_error text-white",
      })
    }
  }

  const getPriceFilter = () => {
    if (!priceIds) return {}
    return { price: { $in: priceIds } }
  }

  const getHostFilters = () => filters.map((filter) => filter.match?.(hostFilterValues[filter.id]) || {})

  const getTypeFilter = () => {
    if (!typeFilter || !typeFilter.length) return {}
    return { type: { $in: typeFilter.map((t) => t.value) } }
  }

  const handleProductClick = (item, tab = "") => {
    if (tab) setProductTab(tab)
    setSelectedInstance(item)
    if (item?.pid) setPid(item.pid)
  }

  const createNewProduct = () => {
    // Initialize new product with defaults from StoreProduct model
    const newProduct = {}
    StoreProduct.fields.forEach((field) => {
      if (field.default !== undefined) {
        newProduct[field.id] = field.default
      }
    })
    setSelectedInstance(newProduct)
  }


  const handlePriceClick = async (item) => {
    if (item.price) {
      // Fetch the full price object with price_model populated
      try {
        const crudAPI = api('crud')
        const priceId = typeof item.price === 'object' ? item.price._id : item.price

        const response = await crudAPI({
          get: {
            model: 'StorePrice',
            match: { _id: priceId },
            limit: 1
          }
        })

        if (Array.isArray(response) && response.length > 0) {
          const loadedPrice = response[0]

          // If this is a purchase product, normalize price model
          const productType = typeof item.type === 'object' ? item.type.value : item.type
          if (productType === 'purchase' && loadedPrice.price_model && Array.isArray(loadedPrice.price_model)) {
            loadedPrice.price_model = loadedPrice.price_model.map(model => ({
              ...model,
              auto_renew: false,
              duration: 0
            }))
          }

          setSelectedPrice({
            ...loadedPrice,
            _parentProduct: item // Keep reference to parent product for saving
          })
        } else {
          showNotification({
            message: t`Failed to load price data`,
            className: "bg-admin_error text-white",
          })
        }
      } catch (error) {
        console.error('Failed to fetch price:', error)
        showNotification({
          message: t`Failed to load price data`,
          className: "bg-admin_error text-white",
        })
      }
    } else {
      // Create empty price with default price model
      // Start with virtual type to avoid platform ID validation, user can change to real later
      setSelectedPrice({
        price_model: [
          {
            type: 'virtual',
            currency: storeCurrency,
            amount: 0,
            duration: 'one-time-payment',
            trial_duration: 'no-trial',
            auto_renew: false,
            primary: true,
            requires_payment_method: false,
            real_product_id: [],
            phases: []
          }
        ],
        _parentProduct: item
      })
    }
  }

  const handlePriceSave = async (updatedPrice) => {
    try {
      // Remove the parent product reference before saving
      const { _parentProduct, ...priceToSave } = updatedPrice

      // If this is for a purchase product, enforce purchase-specific constraints
      if (_parentProduct) {
        const productType = typeof _parentProduct.type === 'object'
          ? _parentProduct.type.value
          : _parentProduct.type

        if (productType === 'purchase') {
          // For purchase products, enforce:
          // 1. auto_renew must be false
          // 2. duration must be set to 0 (one-time payment)
          if (priceToSave.price_model && Array.isArray(priceToSave.price_model)) {
            priceToSave.price_model = priceToSave.price_model.map(model => ({
              ...model,
              auto_renew: false,
              duration: 0
            }))
          }
        }
      }

      // Create the CRUD API instance
      const crudAPI = api('crud')

      // Determine if this is a create or update operation
      const isNewPrice = !priceToSave._id
      const operation = isNewPrice ? 'create' : 'update'

      // Save the price using CRUD
      const response = await crudAPI({
        [operation]: {
          model: 'StorePrice',
          data: priceToSave
        }
      })

      if (response.result) {
        const savedPrice = response.result

        // If this was a new price, update the parent product to link to it
        if (isNewPrice && _parentProduct) {
          const productCrudAPI = api('crud')
          const linkResponse = await productCrudAPI({
            update: {
              model: 'StoreProduct',
              data: {
                _id: _parentProduct._id,
                price: savedPrice._id
              }
            }
          })

          if (linkResponse.error) {
            showNotification({
              message: t("Price created but failed to link to product: %s", linkResponse.error),
              className: "bg-admin_error text-white",
            })
          }
        }

        showNotification({
          message: isNewPrice ? t`Price created and linked successfully` : t`Price updated successfully`,
          className: "bg-admin_success text-white",
        })
        setRefreshKey((prev) => prev + 1)
        // Update the selectedPrice to reflect the saved value
        setSelectedPrice({ ...savedPrice, _parentProduct })

        // Update selectedInstance with the new/updated price reference
        if (selectedInstance && _parentProduct) {
          // Fetch the updated product to get the full price data
          const productCrudAPI = api('crud')
          const productResponse = await productCrudAPI({
            get: {
              model: 'StoreProduct',
              match: { _id: _parentProduct._id },
              populate: ['price', 'licenseModel'],
              limit: 1
            }
          })

          if (Array.isArray(productResponse) && productResponse.length > 0) {
            setSelectedInstance(productResponse[0])
          } else {
            // Fallback: just update the price ID
            setSelectedInstance(prev => ({
              ...prev,
              price: savedPrice._id || savedPrice
            }))
          }
        }

        return response
      } else if (response.error) {
        // Parse validation error and provide user-friendly message
        let errorMessage = response.error

        // Check for price_model validation errors
        if (errorMessage.includes('price_model') && errorMessage.includes('amount') && errorMessage.includes('required')) {
          errorMessage = t`Please add a regular price with amount, currency, and type`
        }

        showNotification({
          message: errorMessage,
          className: "bg-admin_error text-white",
        })
        return null
      }
    } catch (error) {
      console.error('🔴 Save error:', error)
      showNotification({
        message: error.message || t`Failed to save price`,
        className: "bg-admin_error text-white",
      })
      return null
    }
  }

  const handleItemDelete = (item) => {
    showPromptDialog({
      variant: "admin",
      title: labels.confirmDeleteTitle,
      // the product's name as the table shows it (rows from the vendor api carry
      // `name` as text; the store's own rows may carry it per language)
      message: labels.confirmDeleteMessage.replace(
        "%s",
        typeof item.name === "object" ? item.name.en || Object.values(item.name)[0] : item.name || item.pid || item.price_id || labels.thisProduct
      ),
      confirmText: labels.delete,
      cancelText: labels.cancel,
      onConfirm: async () => {
        // Handle delete action
        const response = await SoftDeleteProduct(item)

        if (response.error) {
          showNotification({
            message: response.error,
            className: "bg-admin_error text-white",
          })
          return
        } else {
          showNotification({
            message: labels.deleted,
            className: "bg-admin_primary text-white",
          })
          setRefreshKey((prev) => prev + 1)
        }
      },
    })
  }

  //Save a price item
  const handleItemSave = async (item, { changed } = {}) => {
    const isNewProduct = !item._id

    // An existing product is written by more than this form while the dialog
    // is open — its tabs' own saves, uploads, the review, the server's
    // counters — so only the fields the form changed are sent; the whole form
    // written back reverted all of those
    if (!isNewProduct && Array.isArray(changed)) {
      const fields = changed.filter((key) => key !== "_id" && key in item)
      if (!fields.length) {
        showNotification({ message: t`Product updated successfully`, className: "bg-admin_primary text-white" })
        return { result: item._id }
      }
      item = { _id: item._id, ...Object.fromEntries(fields.map((key) => [key, item[key]])) }
    }

    // If this is a new product (no _id), show confirmation dialog first
    if (isNewProduct) {
      const confirmed = await new Promise((resolve) => {
        showPromptDialog({
          variant: "admin",
          title: t`Create Store Product`,
          message: t`Once a store product is created its Product ID can't be changed. You can always create a new one if needed.`,
          confirmText: t`Proceed`,
          cancelText: t`Go Back`,
          onConfirm: () => resolve(true),
          onCancel: () => resolve(false),
        })
      })

      if (!confirmed) {
        return null
      }

      // For new products, create a default price if none exists
      if (!item.price) {
        const crudAPI = api('crud')
        const defaultPrice = {
          price_model: [
            {
              type: 'virtual',
              currency: storeCurrency,
              amount: 0,
              duration: 0, // Number for one-time payment
              trial_duration: 0, // Number for no trial
              auto_renew: false,
              primary: true,
              requires_payment_method: false,
              real_product_id: [],
              phases: []
            }
          ]
        }

        const priceResponse = await crudAPI({
          create: {
            model: 'StorePrice',
            data: defaultPrice
          }
        })

        if (priceResponse.result) {
          const priceId = priceResponse.result._id || priceResponse.result.id || priceResponse.result
          item.price = priceId
        } else {
          showNotification({
            message: t("Failed to create default price: %s", priceResponse.error),
            className: "bg-admin_error text-white",
          })
          return null
        }
      }
    }

    //save the item
    const response = await SaveStoreProduct(item)

    if (response.error) {
      showNotification({
        message: response.error,
        className: "bg-admin_error text-white",
      })
      return null
    } else {
      showNotification({
        message: isNewProduct ? t`Product created successfully` : t`Product updated successfully`,
        className: "bg-admin_primary text-white",
      })
      setRefreshKey((prev) => prev + 1)
      // The dialog stays open, and everything in it is derived from
      // `selectedInstance`: leaving the pre-save document there means any
      // re-sync of the editor's state resurrects the values just saved over.
      // The CRUD update answers with the id alone, so the product is read back
      // — populated, as the browser loads it.
      const saved = response.result || response.data
      const savedId = (saved && typeof saved === "object" ? saved._id : saved) || item._id
      if (savedId) {
        const res = await GetStoreProducts({ match: { _id: savedId }, limit: 1 })
        const fresh = Array.isArray(res) ? res[0] : res?.data?.[0]
        if (fresh) setSelectedInstance(fresh)
        else if (saved?._id) setSelectedInstance((current) => ({ ...current, ...saved }))
      }
    }

    return response
  }

  const priceLink = (item, children) => (
    <button
      onClick={() => handlePriceClick(item)}
      className="text-admin_primary hover:text-admin_primary_dk font-semibold text-left transition-colors"
    >
      {children}
    </button>
  )

  const formatPrice = (priceItem, item, clickable = false) => {
    //the primary price model, when the product has one
    const priceModel = priceItem?.price_model?.find((c) => c.primary)

    if (!priceModel) {
      if (clickable) return priceLink(item, labels.addPrice)
      return labels.noPriceModel
    }

    const priceContent = (() => {
      // Check if the price is free
      if (parseFloat(priceModel.amount) === 0) {
        return <div className="font-semibold">Free</div>
      }

      let price = priceDisplayFormat(priceModel)

      if (price) {
        return <div className="font-semibold">{price}</div>
      }

      return "Invalid currency"
    })()

    if (clickable) return priceLink(item, priceContent)

    return priceContent
  }

  const productItemRender = [
    {
      id: "name",
      title: labels.productName,
      // no width of its own: the name takes what the other columns leave (the table's min-width keeps it readable)
      sortable: true,
      render: (value, item) => (
        <button
          onClick={() => handleProductClick(item)}
          className="flex items-center gap-3 text-admin_primary hover:text-admin_primary/80 font-semibold text-left transition-colors"
        >
          {item.thumbnail ? (
            <span className="relative shrink-0">
              <img
                src={url(item.thumbnail?.url || item.thumbnail)}
                alt={item.name}
                className="w-[50px] h-[50px] object-cover rounded"
              />
              {thumbnailBadge && <span className="absolute -bottom-1 -right-1">{thumbnailBadge(item)}</span>}
            </span>
          ) : (
            thumbnailBadge && <span className="shrink-0">{thumbnailBadge(item)}</span>
          )}
          {item.name}
          {nameBadge && nameBadge(item)}
        </button>
      ),
    },
    {
      id: "price_model",
      title: labels.price,
      headerClassName: "w-[120px]",
      sortable: true,
      render: (value, item) => {
        return (
          <div className="flex flex-col gap-1">
            <div className="flex flex-col justify-center gap-2">
              <div className="">{formatPrice(item.price, item, true)}</div>
            </div>
          </div>
        )
      },
    },
    //add the Type field
    {
      id: "type",
      title: labels.type,
      headerClassName: "w-[140px]",
      sortable: true,
      render: (value, item) => {
        const typeColor = getProductTypeColor(item.type)

        return (
          <span className={twMerge("inline-flex items-center justify-center text-xs px-2.5 py-0.5 rounded-full min-w-[110px]", typeColor)}>
            {item.type}
          </span>
        )
      },
    },
    {
      id: "platforms",
      title: labels.linkedWithStore,
      headerClassName: "w-[110px] whitespace-nowrap",
      render: (value, item) => {
        const { price } = item

        // If no price, show empty state
        if (!price || !price.price_model || !Array.isArray(price.price_model)) {
          return (
            <div className="text-admin_text_secondary text-sm italic">
              {labels.noPriceConfigured}
            </div>
          )
        }

        // Count price models by platform (check for any price model with platform IDs)
        const platformCounts = {}
        price.price_model
          .filter((priceModel) => priceModel.real_product_id && priceModel.real_product_id.length > 0)
          .forEach((priceModel) => {
            priceModel.real_product_id.forEach((p) => {
              const platform = typeof p.platform === 'object' ? p.platform?.value : p.platform
              if (!platform) return
              platformCounts[platform] = (platformCounts[platform] || 0) + 1
            })
          })

        const platforms = Object.keys(platformCounts).sort((a, b) => a.localeCompare(b))

        // If no platforms configured, show message
        if (platforms.length === 0) {
          return (
            <div className="text-admin_text_secondary text-sm italic">
              {labels.noPlatforms}
            </div>
          )
        }

        return (
          <div className="flex flex-row gap-2">
            {platforms.map((platform, index) => {
              const count = platformCounts[platform]
              return (
                <div key={platform || index} className="relative">
                  <Tooltip content={`${count} ${platform} Price ${count === 1 ? 'ID' : 'IDs'}`}>
                    <div className="relative">
                      {renderPlatformIcon(platform, "w-7 h-7 text-admin_text")}
                      {count > 1 && (
                        <span className="absolute -top-1 -right-1 bg-admin_error text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                          {count}
                        </span>
                      )}
                    </div>
                  </Tooltip>
                </div>
              )
            })}
          </div>
        )
      },
    },
    {
      id: "actions",
      title: labels.actions,
      headerClassName: "w-[80px] text-center",
      render: (value, item) => (
        <div className="flex items-center justify-center gap-1">
          <Button
            variant="admin_icon"
            onClick={() => handleItemDelete(item)}
          >
            <TrashIcon className="w-5 h-5 items-center" />
          </Button>
        </div>
      ),
    },
  ]

  // what can be done to the picked products, and doing it
  const bulkActions = selectionActions && selectedIds.length ? selectionActions({ ids: selectedIds, rows: selectedRows }) || [] : []
  const runBulkAction = async (action) => {
    setBulkBusy(true)
    try {
      await action.run({
        ids: selectedIds,
        rows: selectedRows,
        refresh: () => setRefreshKey((key) => key + 1),
        clear: clearSelection,
      })
    } finally {
      setBulkBusy(false)
    }
  }
  const selectionBar = selectionActions && selectedIds.length > 0 && (
    <div className="w-full flex flex-wrap items-center gap-3 rounded-lg bg-admin_primary/10 px-3 py-2" data-testid="product-selection">
      <Typography variant="admin_body1" className="my-0 font-semibold">
        {labels.selected.replace("%s", selectedIds.length)}
      </Typography>
      <Button variant="admin_secondary" onClick={clearSelection} disabled={bulkBusy}>
        {labels.deselect}
      </Button>
      {bulkActions.length > 0 && (
        <DropDownSelect
          variant="admin"
          placeholder={labels.actions}
          options={bulkActions}
          fixedWidth="340px"
          value={[]}
          showCheckbox={false}
          searchEnabled={false}
          enableResetButton={false}
          disabled={bulkBusy}
          onItemRender={(option) => (
            <div className="flex items-start gap-2.5 py-0.5">
              {option.icon && <option.icon className="w-5 h-5 mt-0.5 shrink-0 text-admin_primary" />}
              <div className="flex flex-col">
                <span className="text-sm font-medium text-admin_text">{option.name}</span>
                {option.hint && <span className="text-xs text-admin_text_secondary">{option.hint}</span>}
              </div>
            </div>
          )}
          onChange={(picked) => {
            const option = Array.isArray(picked) ? picked[0] : picked
            const action = bulkActions.find((entry) => entry.value === option?.value)
            if (action) runBulkAction(action)
          }}
          className="w-[200px]"
          data-testid="product-actions"
        />
      )}
    </div>
  )

  const headerControls = (
      <div className="w-full flex flex-wrap items-center gap-3">
        {selectionBar}
        {filters.map((filter) => (
          <React.Fragment key={filter.id}>
            {filter.render({
              value: hostFilterValues[filter.id],
              onChange: (value) => {
                setHostFilterValues((prev) => ({ ...prev, [filter.id]: value }))
                setRefreshKey((prev) => prev + 1)
              },
            })}
          </React.Fragment>
        ))}

        {!hiddenFilters.includes("type") && <DropDownSelect
          variant="admin"
          options={productTypeOptions}
          multiSelection
          fixedWidth="160px"
          placeholder={labels.filterByType}
          value={typeFilter}
          onChange={(value) => {
            setTypeFilter(value)
            setRefreshKey((prev) => prev + 1)
          }}
        />}

        {!hiddenFilters.includes("platform") && <DropDownSelect
          variant="admin"
          options={[
            { value: "iOS", name: "iOS" },
            { value: "Android", name: "Android" },
            { value: "Web", name: "Web" },
            { value: "Windows", name: "Windows" },
            { value: "Mac", name: "Mac" },
          ]}
          multiSelection
          fixedWidth="180px"
          placeholder={labels.filterByPlatform}
          value={platformFilter}
          onChange={(value) => {
            setPlatformFilter(value)
            setRefreshKey((prev) => prev + 1)
          }}
          showCheckbox={false}
          showSelectionTick={true}
          onItemRender={(platform) => (
            <div className="flex items-center gap-2">
              {renderPlatformIcon(platform.value.toLowerCase(), "w-4 h-4 text-admin_text_secondary")}
              <span className="text-sm text-admin_text">
                {platform.name}
              </span>
            </div>
          )}
          onValueItemRender={(platform) => {
            if (!platform) return labels.filterByPlatform
            return (
              <div className="flex items-center gap-2">
                {renderPlatformIcon(platform.value.toLowerCase(), "w-4 h-4 text-admin_text_secondary")}
                <span className="text-sm">
                  {platform.name}
                </span>
              </div>
            )
          }}
        />}

        {!hiddenFilters.includes("price") && (
          <RangeSelector
            variant="admin"
            className="w-[180px]"
            placeholder={labels.price}
            labels={{ min: labels.priceMin, max: labels.priceMax, range: labels.priceRange }}
            invalidText={labels.priceRangeInvalid}
            value={priceRange}
            onChange={changePriceRange}
          />
        )}
      </div>
  )

  // Field validators are now defined in the StoreProduct model definition
  // See: aldoo-ra/CMSModels/StoreProduct/index.js
  const validators = null

  return (
    <div>
      {/* Header */}
      {!hideHeader && !dialogOnly && <PageHeader
        title="Products"
        actions={
          <>
            <Button
              variant="admin_secondary"
              onClick={() => setShowRestoreDialog(true)}
            >
              <div className="flex items-center gap-2">
                <ArrowUturnLeftIcon className="w-5 h-5" />
                Restore
              </div>
            </Button>

            <Button
              variant="admin_primary"
              className="whitespace-nowrap"
              onClick={createNewProduct}
            >
                <PlusCircleIcon className="w-5 h-5 mr-2" />
                Add
            </Button>
          </>
        }
      />}

      {!dialogOnly && (
      <div className="p-0 md:p-4">
        <DataTable
          variant="admin_browser"
          preferredHeight="70vh"
          apiMethod={GetStoreProducts}
          headerControls={headerControls}
          itemHeight="h-[65px] xl:h-[56px]"
          searchField="name pid"
          searchPlaceholder={labels.search}
          searchObject={{}}
          requestFields={{
            match: {
              $and: [
                {
                  deleted: false,
                },
                getTypeFilter(),
                getPlatformFilter(),
                getPriceFilter(),
                ...getHostFilters(),
                matchProp || {},
              ].filter(condition => Object.keys(condition).length > 0),
            },
          }}
          sort={{ created: -1 }}
          renders={
            // a host may show a column its own way (the merchant portal: the
            // content type for Type, the review state instead of the store link)
            columnOverrides
              ? productItemRender
                  .map((column) => {
                    const override = columnOverrides[column.id]
                    if (!override) return column
                    const render = override.render ? (value, item) => override.render(value, item, { open: (tab) => handleProductClick(item, tab) }) : column.render
                    return { ...column, ...override, render }
                  })
                  .filter((column) => !column.hidden)
              : productItemRender
          }
          refreshKey={refreshKey}
          itemsPerPage={50}
          localUserPreferencesKey="ui.storeProducts"
          enableSelection={!!selectionActions}
          selectedItems={selectionActions ? selectedIds : undefined}
          selectedData={selectionActions ? selectedRows : undefined}
          onSelectionChange={
            selectionActions
              ? (ids, rows) => {
                  setSelectedIds(ids)
                  setSelectedRows(rows)
                }
              : undefined
          }
          styleOverrides={{
            // never narrower than its columns need: a small screen scrolls it sideways rather than
            // squeezing one column into the next
            table: "w-full min-w-[760px] table-fixed",
            // the search and the filters on two rows (in the card's top-right corner the search ran
            // over the filters from md): from md the search first, the filters under it; a phone
            // keeps the filters first, the search right above the rows; from xl all on one row, the
            // filters first and the search taking the rest of it
            header: "w-full flex flex-col xl:flex-row items-stretch xl:items-center gap-3 my-4",
            searchInput: "order-2 md:order-1 xl:order-2 w-full max-w-[564px] xl:max-w-none xl:flex-1 xl:min-w-0 h-auto text-text bg-transparent outline-none",
            headerControls: "order-1 md:order-2 xl:order-1 w-full xl:w-auto xl:shrink-0 flex justify-start",
          }}
          noData={noData || <Typography variant="admin_body1">{labels.noProducts}</Typography>}
        />
      </div>
      )}

      <Modal
        isOpen={selectedInstance !== null}
        onClose={closeDialog}
        variant="admin_fullscreen"
        // on a phone the editor's own back chevron closes it: one control, not two
        styleOverrides={{ closeButton: "absolute right-4 top-4 z-20 hidden md:block" }}
      >
        <EditingLanguageProvider value={editingLanguage}>
        <ContentEditor
          t={t}
          title={selectedInstance?.name || t`Store Product`}
          language={editingLanguage.language}
          actions={EditingLanguageSwitch}
          // the model is composed of extensions: the tabs go in a rail
          tabsOrientation="vertical"
          helpKeyOf={helpKeyOf}
          validators={validators}
          formatters={[StoreProductFormatter]}
          // the id is a field of the Information tab, not a subtitle: the
          // header says where the product stands (the extensions' own actions)
          contentType={StoreProduct}
          dataItem={selectedInstance}
          fieldValues={selectedInstance || {}}
          // the dialog stays open after a save, so the form keeps what it
          // holds: emptied, the next save lost pid and type ("… is required")
          // and Edit Price, finding no _id, asked to save the product again
          clearValuesAfterSave={false}
          showSearch={false}
          // the header actions of the model's extensions close the dialog too
          onClose={closeDialog}
          onBack={closeDialog}
          onError={(errors) => {
            showNotification({
              message: Object.values(errors).find((e) => e),
              className: "bg-admin_error text-white",
            })
          }}
          onSave={handleItemSave}
          onClickSave={(result) => {
            //the dialog stays open after a save — a product is edited tab by
            //tab, and closing it mid-way loses where you were
            if (!result) return
            setRefreshKey((prev) => prev + 1)
          }}
        />
        </EditingLanguageProvider>
      </Modal>

      <Modal
        isOpen={selectedPrice !== null}
        onClose={() => {
          setSelectedPrice(null)
        }}
        variant="admin"
        closeOnEscape
      >
        <ContentEditor
          t={t}
          key={selectedPrice?._id || 'new-price'}
          title={(() => {
            const productName = typeof selectedPrice?._parentProduct?.name === 'object'
              ? (selectedPrice._parentProduct.name.en || selectedPrice._parentProduct.name.default || Object.values(selectedPrice._parentProduct.name)[0])
              : selectedPrice?._parentProduct?.name || t`Price Configuration`
            return productName
          })()}
          contentType={StorePrice}
          dataItem={selectedPrice}
          fieldValues={selectedPrice || {}}
          // one scroll of sections rather than tabs: a price is small enough to see whole
          renderGroupsAs="foldable"
          showSearch={false}
          showExport={true}
          showCopyPaste={true}
          onError={(errors) => {
            const errorMessage = Object.values(errors).find((e) => e)
            if (errorMessage) {
              // Parse validation error and provide user-friendly message
              let message = errorMessage
              if (message.includes('price_model') && message.includes('amount') && message.includes('required')) {
                message = t`Please add a regular price with amount, currency, and type`
              }

              showNotification({
                message,
                className: "bg-admin_error text-white",
              })
            }
          }}
          onSave={handlePriceSave}
          onClickSave={async (result) => {
            // Only close modal if save was successful
            if (result) {
              setSelectedPrice(null)
            }
            // If result is null/false, keep modal open so user can fix errors
          }}
          onFieldChange={(fieldId, value) => {
            // Field change handler
          }}
        />
      </Modal>

      <StoreProductRestoreDialog
        isOpen={showRestoreDialog}
        onClose={() => setShowRestoreDialog(false)}
        onRefresh={() => setRefreshKey((prev) => prev + 1)}
      />
    </div>
  )
}
